import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppButton from "../common/AppButton";
import { SquarePen, MoreVertical, Plus, Trash, Check, X, UserRound, ShoppingCart } from "lucide-react";
import { Reorder } from "framer-motion";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  Tooltip,
} from "@heroui/react";
import { useDispatch } from "react-redux";
import { editHeadingAsync } from "../../redux/heading";
import FiltersBar from "./filters/FiltersBar";
import EditParagraphMetaModal from "./paragraph/EditParagraphModal";
import {
  ensureSelectedItemsStoreForCurrentUser,
  loadSelectedItemsForCurrentUser,
  persistSelectedItemsStoreForCurrentUser,
} from "../../services/userScopedStorage";

// Global store for selected items per session (using window object)
// Initialize at module level to ensure it persists across remounts
if (typeof window !== "undefined") {
  ensureSelectedItemsStoreForCurrentUser();
}

// Helper function to persist store to localStorage
const persistStoreToLocalStorage = () => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const store = ensureSelectedItemsStoreForCurrentUser();
    persistSelectedItemsStoreForCurrentUser(store);
  } catch (e) {
    console.error("[NavigationBox] Failed to save to localStorage:", e);
  }
};

const getSpeakerDisplayLabel = (speakerName, speakerNumber) => {
  const normalizedName = String(speakerName ?? "").trim();
  const hasSpeakerName =
    normalizedName.length > 0 && normalizedName.toLowerCase() !== "no";
  return hasSpeakerName
    ? normalizedName
    : `Speaker ${speakerNumber ?? ""}`.trim();
};

const getHeadingLevel = (headingObj) => {
  const raw = headingObj?.type;
  if (!raw) return null;
  const m = /(\d+)/.exec(String(raw));
  return m ? Number(m[1]) : null;
};

const getLevelBadgeClasses = (level) => {
  switch (level) {
    case 1:
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case 2:
      return "bg-sky-100 text-sky-800 border-sky-200";
    case 3:
      return "bg-amber-100 text-amber-800 border-amber-200";
    case 4:
      return "bg-rose-100 text-rose-800 border-rose-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

// Row component to satisfy hooks rules (useDragControls inside its own component)
const HeadingRow = React.memo(function HeadingRow({
  group,
  headingStart,
  formatTime,
  activeParagraphId,
  onJumpToRefIndex,
  getRefIndexByParagraphId,
  onEditHeading,
  onDragStart,
  onDragEnd,
  onDeleteHeading,
  checked,
  onCheckboxChange,
  isInlineEditing,
  inlineValue,
  onInlineValueChange,
  onInlineSave,
  onInlineCancel,
  isInlineSaving,
  onStartInlineEdit,
}) {
  const headingLevel = getHeadingLevel(group.headingObj);
  return (
    <Reorder.Item
      key={group.key}
      value={group}
      id={`nav-group-${group.key}`}
      onDragStart={() => onDragStart(group.key)}
      onDragEnd={onDragEnd}
      className="mb-2 w-full"
      dragListener={!isInlineEditing}
    >
      {isInlineEditing ? (
        <div className="w-full flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
          <input
            type="text"
            className="flex-1 bg-transparent text-sm text-onSurface placeholder:text-gray-400 focus:outline-none"
            value={inlineValue}
            autoFocus
            onFocus={(e) => e.target.select()}
            onChange={(e) => onInlineValueChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isInlineSaving) {
                e.preventDefault();
                onInlineSave?.();
              } else if (e.key === "Escape") {
                e.preventDefault();
                onInlineCancel?.();
              }
            }}
          />
          <Button
            isIconOnly
            size="sm"
            className="rounded-full bg-errorContainer text-onErrorContainer hover:bg-error hover:text-onError"
            onPress={onInlineCancel}
          >
            <X className="w-4 h-4" />
          </Button>
          <Button
            isIconOnly
            size="sm"
            className="rounded-full bg-secondaryContainer text-onSecondaryContainer hover:bg-secondary hover:text-onSecondary"
            onPress={onInlineSave}
            isLoading={isInlineSaving}
            isDisabled={!inlineValue?.trim() || isInlineSaving}
          >
            <Check className="w-4 h-4" />
          </Button>
      </div>
      ) : (
        <div
          className="text-sm font-semibold cursor-pointer hover:underline flex items-center justify-between w-full gap-2 min-w-0"
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (group.headingObj?.id != null) {
              if (onStartInlineEdit) onStartInlineEdit(group.headingObj, group.title);
            }
          }}
        >
          <span className="flex items-center gap-2 min-w-0">
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox
                size="sm"
                radius="sm"
                color="secondary"
                classNames={{
                  wrapper: "bg-secondaryContainer data-[selected=true]:bg-secondary",
                  icon: "text-onSecondaryContainer data-[selected=true]:text-onSecondary"
                }}
                isSelected={!!checked}
                onValueChange={(val) => onCheckboxChange?.(val)}
              />
            </span>
            <span
              className={`shrink-0 inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getLevelBadgeClasses(
                headingLevel
              )}`}
              aria-label={headingLevel ? `Heading level ${headingLevel}` : "Heading level"}
              title={headingLevel ? `Heading level ${headingLevel}` : "Heading level"}
            >
              {headingLevel ? `L${headingLevel}` : "L"}
            </span>
            <span className="truncate min-w-0">{group.title}</span>
          </span>
          <span className="ms-2 flex items-center gap-2">
            {group.headingObj?.id != null && (
              <Dropdown className="text-onSurface bg-surfaceContainer p-0 border-0">
                <DropdownTrigger>
                  <Button
                    isIconOnly
                    className="h-8 w-8 bg-surface rounded-full p-0 hover:bg-gray-200"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  className="p-0"
                  aria-label="Section actions"
                  onAction={(key) => {
                    if (key === "edit") {
                      onEditHeading?.(group.headingObj);
                    } else if (key === "delete") {
                      onDeleteHeading?.(group.headingObj, group.key);
                    }
                  }}
                >
                  <DropdownItem
                    key="edit"
                    startContent={<SquarePen className="w-4 h-4" />}
                    className="text-sm p-4 py-3 rounded-xl"
                    textValue="Bearbeiten"
                  >
                    Bearbeiten
                  </DropdownItem>
                  <DropdownItem
                    key="delete"
                    startContent={<Trash className="w-4 h-4" />}
                    className="text-sm p-4 py-3 rounded-xl"
                    color="danger"
                    textValue="Löschen"
                  >
                    Löschen
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </span>
        </div>
      )}
    </Reorder.Item>
  );
});

const NavigationBox = ({
  navData,
  getRefIndexByParagraphId,
  onJumpToRefIndex,
  activeParagraphId,
  getHeadingStartByBeforeId,
  getParagraphStartById,
  onEditHeading,
  onCommitReorder,
  onDeleteHeading,
  onAddHeading,
  onMergeSelected,
  onInlineHeadingRename,
  sessionName,
  filtersExpanded,
  onToggleFilters,
  filterSpeakers,
  selectedFilterSpeakers,
  onFilterSpeakersChange,
  headingOptions,
  selectedHeadingFilters,
  onHeadingFilterChange,
  selectedGroupByKeys,
  onGroupByChange,
  onToggleSpeakersModal,
  onToggleAllSpeakers,
  areAllSpeakersOpen,
  sessionId,
  sessionStart,
  speakers,
}) => {
  const dispatch = useDispatch();
  // Absolute wall-clock time = sessionStart + offset. Falls back to
  // the legacy M:SS relative format if sessionStart isn't available.
  const formatTime = (seconds) => {
    const safe = Number(seconds) || 0;
    if (sessionStart) {
      const startDate = new Date(sessionStart);
      if (!Number.isNaN(startDate.getTime())) {
        const wallClock = new Date(startDate.getTime() + safe * 1000);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(wallClock.getUTCHours())}:${pad(wallClock.getUTCMinutes())}:${pad(wallClock.getUTCSeconds())}`;
      }
    }
    const mins = Math.floor(safe / 60);
    const secs = Math.floor(safe % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const toggleSelectAll = () => {
    setSelectedParagraphIds((prev) => {
      const paragraphIds = ordered
        .filter((item) => item.type === "paragraph" && item.paragraph?.id != null)
        .map((item) => item.paragraph.id);
      if (prev.size === paragraphIds.length) {
        return new Set();
      }
      return new Set(paragraphIds);
    });
  };

  // Build a flat interleaved list of headings and paragraphs
  // Each item: { key, type: 'heading'|'paragraph', headingObj?, beforeId?, paragraphs? (unused), paragraph? }
  const flatItems = [];
  navData.forEach((section) => {
    // Section heading
    if (section?.heading2) {
      flatItems.push({
        key: section.heading2.id,
        type: "heading",
        title: section.heading2.content || "Section",
        beforeId: section.heading2.before_paragraph_id ?? null,
        headingObj: section.heading2,
        // best-effort jump target: first paragraph ref index after this heading in initial data
        firstRefIndex: (() => {
          const firstPara = (section.children || []).find((c) => c?.type === "paragraph");
          return firstPara ? getRefIndexByParagraphId?.(firstPara.id) : null;
        })(),
      });
    }
    // Children in order
    for (const child of section.children || []) {
      if (child?.type === "paragraph") {
        flatItems.push({
          key: `p-${child.id}`,
          type: "paragraph",
          paragraph: child,
        });
      } else {
        // subheading
        flatItems.push({
          key: child.id,
          type: "heading",
          title: child.content || "Heading",
          beforeId: child.before_paragraph_id ?? null,
          headingObj: child,
          firstRefIndex: (() => {
            // nearest next paragraph following this subheading in initial data
            return null;
          })(),
        });
      }
    }
  });

  // If multiple headings are directly before the same paragraph, sort consecutive heading blocks by type (Heading 1, Heading 2, ...)
  const headingTypeRank = (t) => {
    if (!t) return 999;
    const m = /(\d+)/.exec(String(t));
    return m ? parseInt(m[1], 10) : 999;
  };
  const sortHeadingBlocksByType = (items) => {
    const res = [];
    let i = 0;
    while (i < items.length) {
      if (items[i].type === "heading") {
        let j = i;
        const block = [];
        while (j < items.length && items[j].type === "heading") {
          block.push(items[j]);
          j++;
        }
        block.sort((a, b) => headingTypeRank(a?.headingObj?.type) - headingTypeRank(b?.headingObj?.type));
        res.push(...block);
        i = j;
      } else {
        res.push(items[i]);
        i++;
      }
    }
    return res;
  };

  // Track locally removed headings to keep them hidden without refetch
  const [removedIds, setRemovedIds] = useState(new Set());

  const visibleFlatItems = useMemo(
    () => flatItems.filter((it) => it.type !== "heading" || !removedIds.has(it.key)),
    // flatItems is derived from navData, so depend on navData + removedIds
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navData, removedIds]
  );

  // Reorderable state and DnD handlers
  const [ordered, setOrdered] = useState(sortHeadingBlocksByType(visibleFlatItems));
  
  // Selection state for paragraphs (speaker entries)
  // Initialize from global store if available
  const [selectedParagraphIds, setSelectedParagraphIds] = useState(() => {
    // Initialize store if needed
    ensureSelectedItemsStoreForCurrentUser();
    if (!sessionId) return new Set();
    const sessionIdStr = String(sessionId);
    let existingItems = window.selectedItemsStore.get(sessionIdStr);
    
    // If not in memory, try localStorage directly
    if (!existingItems || !Array.isArray(existingItems) || existingItems.length === 0) {
      try {
        const parsed = loadSelectedItemsForCurrentUser();
        if (parsed[sessionIdStr] && Array.isArray(parsed[sessionIdStr])) {
            existingItems = parsed[sessionIdStr];
            window.selectedItemsStore.set(sessionIdStr, existingItems);
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    
    const initialSet = existingItems ? new Set(existingItems) : new Set();
    return initialSet;
  });
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [mergeSpeakerKey, setMergeSpeakerKey] = useState(null);
  const listContainerRef = useRef(null);
  const itemRefs = useRef(new Map());
  const lastAutoScrolledParagraphRef = useRef(null);

  useEffect(() => {
    if (activeParagraphId == null) return;
    if (lastAutoScrolledParagraphRef.current === activeParagraphId) return;
    const container = listContainerRef.current;
    const el = itemRefs.current.get(activeParagraphId);
    if (!container || !el) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const currentScroll = container.scrollTop;
    const offsetTop = elRect.top - containerRect.top + currentScroll;
    const targetTop = offsetTop - (containerRect.height / 2 - elRect.height / 2);
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const nextTop = Math.min(Math.max(0, targetTop), maxTop);
    container.scrollTo({ top: nextTop, behavior: "smooth" });
    lastAutoScrolledParagraphRef.current = activeParagraphId;
  }, [activeParagraphId, ordered, filtersExpanded]);
  const [inlineEditHeading, setInlineEditHeading] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState("");
  const [isInlineSaving, setIsInlineSaving] = useState(false);
  const [editParagraph, setEditParagraph] = useState(null);
  const [isEditParagraphOpen, setIsEditParagraphOpen] = useState(false);
  const draggingIdRef = useRef(null);
  useEffect(() => {
    // Preserve the user's current order whenever possible.
    // Only rebuild when items are added/removed, otherwise merge updated fields in-place.
    setOrdered((prev) => {
      if (draggingIdRef.current) return prev;
      const next = sortHeadingBlocksByType(visibleFlatItems);
      const byKey = new Map(next.map((it) => [it.key, it]));
      if (!prev || prev.length === 0) return next;
      const prevKeys = prev.map((i) => i.key);
      const sameLength = prevKeys.length === next.length;
      const sameKeySet =
        sameLength && prevKeys.every((k) => byKey.has(k));
      // Preserve user order as long as the same items exist; only merge updated props.
      if (sameKeySet) {
        // Check if anything actually changed before creating new objects
        const hasChanges = prev.some((it) => byKey.get(it.key) !== it);
        if (!hasChanges) return prev; // nothing changed, avoid re-render
        return prev.map((it) => byKey.get(it.key) ?? it);
      }
      // If items were added/removed, rebuild strictly following the new order while merging prior item state.
      const prevByKey = new Map(prev.map((item) => [item.key, item]));
      return next.map((item) => {
        const existing = prevByKey.get(item.key);
        return existing ? { ...existing, ...item } : item;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navData, visibleFlatItems]);

  const toggleParagraphSelection = useCallback((id, checked) => {
    setSelectedParagraphIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const getHeadingRank = (item) => headingTypeRank(item?.headingObj?.type);

  // Precompute heading selection state for all headings in one pass (avoids O(n²) per render)
  const headingSelectionMap = useMemo(() => {
    const map = new Map();
    // First pass: compute paragraph IDs for each heading index
    const headingParagraphIds = new Map();
    for (let idx = 0; idx < ordered.length; idx++) {
      const current = ordered[idx];
      if (!current || current.type !== "heading") continue;
      const currentRank = getHeadingRank(current);
      const ids = [];
      for (let i = idx + 1; i < ordered.length; i++) {
        const item = ordered[i];
        if (!item) break;
        if (item.type === "heading") {
          const nextRank = getHeadingRank(item);
          if (nextRank <= currentRank) break;
          continue;
        }
        if (item.type === "paragraph" && item.paragraph?.id != null) {
          ids.push(item.paragraph.id);
        }
      }
      headingParagraphIds.set(idx, ids);
    }
    // Second pass: determine selection state
    for (const [idx, ids] of headingParagraphIds) {
      if (ids.length > 0) {
        map.set(idx, ids.every((id) => selectedParagraphIds.has(id)));
      } else {
        // Walk up to nearest ancestor with paragraphs
        let currentIdx = idx;
        let found = false;
        while (currentIdx != null) {
          const current = ordered[currentIdx];
          if (!current || current.type !== "heading") break;
          const currentRank = getHeadingRank(current);
          let ancestorIdx = null;
          for (let i = currentIdx - 1; i >= 0; i--) {
            const item = ordered[i];
            if (item?.type !== "heading") continue;
            const rank = getHeadingRank(item);
            if (rank < currentRank) { ancestorIdx = i; break; }
          }
          if (ancestorIdx == null) { map.set(idx, false); found = true; break; }
          const ancestorIds = headingParagraphIds.get(ancestorIdx);
          if (ancestorIds && ancestorIds.length > 0) {
            map.set(idx, ancestorIds.every((id) => selectedParagraphIds.has(id)));
            found = true;
            break;
          }
          currentIdx = ancestorIdx;
        }
        if (!found) map.set(idx, false);
      }
    }
    return map;
  }, [ordered, selectedParagraphIds]);

  const toggleHeadingSelection = useCallback((idx, checked) => {
    const current = ordered[idx];
    if (!current || current.type !== "heading") return;
    const currentRank = getHeadingRank(current);
    const ids = [];
    for (let i = idx + 1; i < ordered.length; i++) {
      const item = ordered[i];
      if (!item) break;
      if (item.type === "heading") {
        const nextRank = getHeadingRank(item);
        if (nextRank <= currentRank) break;
        continue;
      }
      if (item.type === "paragraph" && item.paragraph?.id != null) {
        ids.push(item.paragraph.id);
      }
    }
    if (ids.length === 0) return;
    setSelectedParagraphIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, [ordered]);

  const getNextParagraphId = (items, startIdx) => {
    for (let i = startIdx + 1; i < items.length; i++) {
      if (items[i]?.type === "paragraph") return items[i].paragraph?.id ?? null;
    }
    return null;
  };

  const handleDragStart = (id) => {
    draggingIdRef.current = id;
  };
  const handleDragEnd = () => {
    const movedId = draggingIdRef.current;
    draggingIdRef.current = null;
    if (!movedId) return;
    const newIdx = ordered.findIndex((g) => g.key === movedId);
    if (newIdx === -1) return;
    const movedItem = ordered[newIdx];
    if (movedItem?.type !== "heading") return;
    // Find the nearest NEXT paragraph to anchor "before_paragraph_id"
    const targetParaId = getNextParagraphId(ordered, newIdx);
    if (!targetParaId) return;
    const seconds = getParagraphStartById?.(targetParaId);
    if (movedItem?.headingObj && typeof seconds === "number") {
      onCommitReorder?.(movedItem.headingObj, targetParaId, seconds);
      // Optimistically update local ordered state so time updates immediately
      setOrdered((prev) => {
        const copy = prev.slice();
        const idx = copy.findIndex((it) => it.key === movedId);
        if (idx !== -1) {
          copy[idx] = { ...copy[idx], beforeId: targetParaId };
        }
        return copy;
      });
    }
  };

  // ---- Merge helpers ----
  const selectedParagraphMeta = React.useMemo(() => {
    const entries = [];
    let paragraphCounter = -1;
    ordered.forEach((item, idx) => {
      if (item.type === "paragraph") {
        paragraphCounter += 1;
        const paragraph = item.paragraph;
        if (paragraph?.id != null && selectedParagraphIds.has(paragraph.id)) {
          entries.push({ orderedIndex: idx, sequenceIndex: paragraphCounter, paragraph });
        }
      }
    });
    return entries;
  }, [ordered, selectedParagraphIds]);

  // Track previous selected items to prevent unnecessary updates
  const prevSelectedItemsRef = useRef(new Set(selectedParagraphIds));
  const prevSessionIdRef = useRef(null);
  const isInitialMountRef = useRef(true);
  
  // Load selected items from store when sessionId changes or on mount
  useEffect(() => {
    if (!sessionId || sessionId === 'undefined' || sessionId === 'null') {
      return;
    }
    
    // Initialize store if needed
    ensureSelectedItemsStoreForCurrentUser();
    
    const sessionIdStr = String(sessionId);
    
    // Load from store on mount or when sessionId changes
    // Check if we need to load (either sessionId changed, or this is the first time with this sessionId)
    const shouldLoad = prevSessionIdRef.current === null || prevSessionIdRef.current !== sessionId;
    
    if (shouldLoad) {
      let existingItems = window.selectedItemsStore.get(sessionIdStr);
      
      // If not found in memory store, try localStorage directly
      if (!existingItems || !Array.isArray(existingItems) || existingItems.length === 0) {
        try {
          const parsed = loadSelectedItemsForCurrentUser();
          if (parsed[sessionIdStr] && Array.isArray(parsed[sessionIdStr])) {
            existingItems = parsed[sessionIdStr];
            // Update memory store
            window.selectedItemsStore.set(sessionIdStr, existingItems);
          }
        } catch (e) {
          console.error('[NavigationBox] Failed to load from localStorage:', e);
        }
      }

      if (existingItems && Array.isArray(existingItems) && existingItems.length > 0) {
        const itemsSet = new Set(existingItems);
        setSelectedParagraphIds(itemsSet);
        prevSelectedItemsRef.current = new Set(itemsSet);
      } else {
        // Only clear if we're switching sessions, not on initial mount with empty store
        // On mount, the useState initializer already set the correct value
        if (prevSessionIdRef.current !== null) {
          const emptySet = new Set();
          setSelectedParagraphIds(emptySet);
          prevSelectedItemsRef.current = new Set(emptySet);
        } else {
          // On mount, sync the ref with current state (from useState initializer)
          prevSelectedItemsRef.current = new Set(selectedParagraphIds);
        }
      }
      prevSessionIdRef.current = sessionId;
      // Reset initial mount flag when sessionId changes
      isInitialMountRef.current = true;
    }
  }, [sessionId]); // Only depend on sessionId, not selectedParagraphIds to avoid loops
  
  // Update global store and listen for queries about selected items
  // Debounce localStorage writes to avoid blocking the main thread
  const persistTimerRef = useRef(null);
  useEffect(() => {
    // Don't do anything if sessionId is undefined or invalid
    if (!sessionId || sessionId === 'undefined' || sessionId === 'null') {
      return;
    }

    // Initialize store if needed
    ensureSelectedItemsStoreForCurrentUser();

    const sessionIdStr = String(sessionId);
    const selectedItems = Array.from(selectedParagraphIds);
    const selectedItemsSet = new Set(selectedItems);

    // Skip if this is the initial mount and store already has data (to avoid overwriting)
    // But only skip if the data matches what we have
    if (isInitialMountRef.current) {
      const existingInStore = window.selectedItemsStore.get(sessionIdStr);
      if (existingInStore && Array.isArray(existingInStore) && existingInStore.length > 0) {
        const existingSet = new Set(existingInStore);
        // Only skip if the sets are equal
        if (existingSet.size === selectedItemsSet.size &&
            Array.from(existingSet).every(id => selectedItemsSet.has(id))) {
          isInitialMountRef.current = false;
          prevSelectedItemsRef.current = new Set(existingSet);
          return;
        }
      }
      isInitialMountRef.current = false;
    }

    // Always update the in-memory store immediately (fast)
    prevSelectedItemsRef.current = new Set(selectedItemsSet);
    window.selectedItemsStore.set(sessionIdStr, selectedItems);

    // Debounce localStorage writes (500ms) to avoid blocking main thread on rapid clicks
    clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persistStoreToLocalStorage();
    }, 500);

    // Defer event dispatch to next tick
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('selected-items-store-updated'));
    }, 0);

    const handleQuerySelectedItems = (event) => {
      const { sessionId: querySessionId } = event.detail;
      // Compare as strings to handle number/string mismatches
      if (String(querySessionId) === String(sessionId)) {
        const currentSelectedItems = Array.from(selectedParagraphIds);
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('selected-items-response', {
            detail: {
              sessionId: String(sessionId),
              selectedItems: currentSelectedItems
            }
          }));
        }, 0);
      }
    };

    window.addEventListener('query-selected-items', handleQuerySelectedItems);

    // Cleanup: Save state on unmount
    return () => {
      window.removeEventListener('query-selected-items', handleQuerySelectedItems);
      // Flush any pending debounced write immediately on unmount
      clearTimeout(persistTimerRef.current);
      if (sessionId && sessionId !== 'undefined' && sessionId !== 'null') {
        const finalSelectedItems = Array.from(selectedParagraphIds);
        const finalSessionIdStr = String(sessionId);
        if (window.selectedItemsStore) {
          window.selectedItemsStore.set(finalSessionIdStr, finalSelectedItems);
          persistStoreToLocalStorage();
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('selected-items-store-updated'));
          }, 0);
        }
      }
    };
  }, [sessionId, selectedParagraphIds]);

  const isSelectionContiguous = React.useMemo(() => {
    if (selectedParagraphMeta.length < 2) return false;
    const order = selectedParagraphMeta
      .map((entry) => entry.sequenceIndex)
      .sort((a, b) => a - b);
    for (let i = 1; i < order.length; i++) {
      if (order[i] !== order[i - 1] + 1) return false;
    }
    return true;
  }, [selectedParagraphMeta]);

  const selectedSpeakerOptions = React.useMemo(() => {
    const map = new Map();
    selectedParagraphMeta.forEach(({ paragraph }) => {
      if (!paragraph) return;
      const label = getSpeakerDisplayLabel(
        paragraph.speaker_name,
        paragraph.speaker_number
      );
      const normalizedName = String(paragraph.speaker_name ?? "").trim();
      const hasSpeakerName =
        normalizedName.length > 0 && normalizedName.toLowerCase() !== "no";
      const key = `${hasSpeakerName ? normalizedName : "no"}::${paragraph.speaker_number ?? ""}`;
      if (!map.has(key)) map.set(key, label);
    });
    return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
  }, [selectedParagraphMeta]);

  const beginInlineEdit = (headingObj, fallbackTitle) => {
    if (!headingObj || headingObj.id == null) return;
    setInlineEditHeading(headingObj);
    setInlineEditValue(fallbackTitle ?? headingObj.content ?? "");
  };

  const cancelInlineEdit = () => {
    setInlineEditHeading(null);
    setInlineEditValue("");
    setIsInlineSaving(false);
  };

  const handleInlineSave = async () => {
    if (inlineEditHeading?.id == null) return;
    const nextTitle = inlineEditValue.trim();
    if (!nextTitle) return;
    try {
      setIsInlineSaving(true);
      if (typeof onInlineHeadingRename === "function") {
        await onInlineHeadingRename(inlineEditHeading, nextTitle);
      } else {
        await dispatch(
          editHeadingAsync({
            id: inlineEditHeading.id,
            payload: { content: nextTitle },
          })
        );
      }
      setOrdered((prev) =>
        prev.map((item) => {
          if (item.type === "heading" && item.headingObj?.id === inlineEditHeading.id) {
            return {
              ...item,
              title: nextTitle,
              headingObj: { ...item.headingObj, content: nextTitle },
            };
          }
          return item;
        })
      );
      cancelInlineEdit();
    } catch (error) {
      console.error("Failed to rename heading", error);
    } finally {
      setIsInlineSaving(false);
    }
  };

  const openMergeModal = () => {
    if (!isSelectionContiguous) return;
    setMergeSpeakerKey(selectedSpeakerOptions[0]?.key ?? null);
    setIsMergeOpen(true);
  };

  const handleConfirmMerge = () => {
    const orderedSelection = [...selectedParagraphMeta].sort(
      (a, b) => a.sequenceIndex - b.sequenceIndex
    );
    const ids = orderedSelection.map(({ paragraph }) => paragraph?.id).filter((id) => id != null);
    if (ids.length >= 2 && mergeSpeakerKey) {
      onMergeSelected?.(ids, mergeSpeakerKey);
      // Clear selection after merge trigger
      setSelectedParagraphIds(new Set());
      setIsMergeOpen(false);
    }
  };

  if (!Array.isArray(navData) || navData.length === 0) return null;

  const sessionLabel = sessionName || "Sitzung";

  return (
    <div className="hidden md:block w-[27rem] shrink-0 border-r border-gray-200 px-3 py-2 bg-surface rounded-xl mt-2 grid grid-rows-[auto,auto,1fr] h-[86vh] overflow-hidden">
      <div className=" ">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase text-onSurface/60">Sitzung</p>
            <p className="text-base font-semibold text-onSurface truncate">{sessionLabel}</p>
          </div>
          {onToggleFilters && (
            <AppButton
              className="mt-3"
              onClick={onToggleFilters}
              width="w-[140px]"
              aria-expanded={filtersExpanded}
            >
              {filtersExpanded ? "Filter ausblenden" : "Filter"}
            </AppButton>
          )}
        </div>
        {filtersExpanded && (
          <div className="rounded-2xl border border-outlineVariant bg-surfaceContainerLow/80 p-3">
            <FiltersBar
              speakers={filterSpeakers}
              selectedSpeakers={selectedFilterSpeakers}
              onSpeakersChange={onFilterSpeakersChange}
              headings={headingOptions}
              selectedHeadings={selectedHeadingFilters}
              onHeadingsChange={onHeadingFilterChange}
              selectedGroupBy={selectedGroupByKeys}
              onGroupByChange={onGroupByChange}
              expanded={filtersExpanded}
            />
            <div className="mt-3 flex gap-2">
              <AppButton
                onClick={() => {
                  onFilterSpeakersChange?.(new Set());
                  onHeadingFilterChange?.(new Set());
                  onGroupByChange?.(new Set(["Heading 2"]));
                }}
                width="w-full"
                aria-label="Filter zurücksetzen"
              >
                Filter zurücksetzen
              </AppButton>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {/* {onToggleAllSpeakers && (
            <AppButton
              onClick={onToggleAllSpeakers}
              width="w-[170px]"
              aria-label={areAllSpeakersOpen ? "Close all speakers" : "Open all speakers"}
            >
              {areAllSpeakersOpen ? "Close all speakers" : "Open all speakers"}
            </AppButton>
          )}
          {onToggleSpeakersModal && (
            <AppButton onClick={onToggleSpeakersModal} width="w-[120px]">
              Speakers
            </AppButton>
          )} */}
        </div>
      </div>
      <div className="pb-2 pt-2">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <Tooltip content="Sprecherbeiträge auswählen, um Zusammenfassungen zu erstellen oder Einträge zusammenzuführen" placement="bottom">
            <div>
              <AppButton
                onClick={toggleSelectAll}
                width="w-auto"
                className="py-1 px-2 gap-1"
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                Alle
              </AppButton>
            </div>
          </Tooltip>
          <div className="flex items-center gap-2">
            <AppButton
              onClick={openMergeModal}
              width="w-[50px]"
              roundedFull="rounded-full"
              className={`p-1 ${!isSelectionContiguous ? "opacity-50 cursor-not-allowed" : ""}`}
              aria-label="Merge selected"
              title="Merge selected"
              disabled={!isSelectionContiguous}
            >
              <UserRound className="w-4 h-4" />
              <UserRound className="w-4 h-4" />
            </AppButton>
            <AppButton
              onClick={() => onAddHeading?.()}
              width="w-8"
              roundedFull="rounded-full"
              className="p-1"
              aria-label="Add section"
              title="Add section"
            >
              <Plus className="w-4 h-4" />
            </AppButton>
          </div>
        </div>
      </div>
      <div
        ref={listContainerRef}
        className={`min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pe-2 ${filtersExpanded ? 'h-[55vh]' : 'h-[71vh]'}`}
      >
        <Reorder.Group axis="y" values={ordered} onReorder={setOrdered} className="m-0 p-0 list-none">
          {ordered.map((group, idx) => {
            if (group.type === "heading") {
              const headingStart = group.beforeId != null
                ? (getParagraphStartById?.(group.beforeId) ?? getHeadingStartByBeforeId?.(group.beforeId) ?? null)
                : null;
              return (
                <HeadingRow
                  key={group.key}
                  group={group}
                  headingStart={headingStart}
                  formatTime={formatTime}
                  activeParagraphId={activeParagraphId}
                  onJumpToRefIndex={onJumpToRefIndex}
                  getRefIndexByParagraphId={getRefIndexByParagraphId}
                  onEditHeading={onEditHeading}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDeleteHeading={(heading) => {
                    setRemovedIds((prev) => {
                      const next = new Set(prev);
                      next.add(group.key);
                      return next;
                    });
                    setOrdered((prev) => prev.filter((it) => it.key !== group.key));
                    onDeleteHeading?.(heading);
                  }}
                  checked={!!headingSelectionMap.get(idx)}
                  onCheckboxChange={(checked) => toggleHeadingSelection(idx, checked)}
                  isInlineEditing={
                    inlineEditHeading?.id != null &&
                    group.headingObj?.id != null &&
                    inlineEditHeading.id === group.headingObj.id
                  }
                  inlineValue={inlineEditValue}
                  onInlineValueChange={setInlineEditValue}
                  onInlineSave={handleInlineSave}
                  onInlineCancel={cancelInlineEdit}
                  isInlineSaving={isInlineSaving}
                  onStartInlineEdit={(heading, fallbackTitle) => beginInlineEdit(heading, fallbackTitle)}
                />
              );
            } else {
              const p = group.paragraph;
              const isActive = activeParagraphId != null && p.id === activeParagraphId;
              return (
                <Reorder.Item
                  key={group.key}
                  value={group}
                  dragListener={false}
                  className="mb-1 w-full"
                >
                  <li
                    ref={(el) => {
                      if (!p?.id) return;
                      if (el) itemRefs.current.set(p.id, el);
                      else itemRefs.current.delete(p.id);
                    }}
                    className={`text-xs text-onSurface cursor-pointer hover:underline flex items-center justify-between gap-2 w-full px-0 py-1 ${isActive ? "bg-secondaryContainer rounded-md" : ""}`}
                    onClick={() => {
                      const refIdx = getRefIndexByParagraphId?.(p.id);
                      if (refIdx) onJumpToRefIndex?.(refIdx);
                    }}
                  >
                    <span className="flex items-center gap-2 min-w-0 truncate">
                      <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="sm"
                          radius="sm"
                          color="secondary"
                          classNames={{
                            wrapper: "bg-secondaryContainer data-[selected=true]:bg-secondary",
                            icon: "text-onSecondaryContainer data-[selected=true]:text-onSecondary"
                          }}
                          isSelected={selectedParagraphIds.has(p.id)}
                          onValueChange={(val) => toggleParagraphSelection(p.id, val)}
                        />
                      </span>
                      <span className="truncate min-w-0">
                        {getSpeakerDisplayLabel(p.speaker_name, p.speaker_number)}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <button
                        type="button"
                        className="h-5 w-5 inline-flex items-center justify-center rounded-full text-onSurface/70 hover:text-onSurface hover:bg-secondaryContainer"
                        aria-label="Sprecher verwalten"
                        title="Sprecher verwalten"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditParagraph(p);
                          setIsEditParagraphOpen(true);
                        }}
                      >
                        <SquarePen className="w-3 h-3" />
                      </button>
                      <span className="me-1 text-gray-600 font-mono" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatTime(p.start)}
                      </span>
                    </span>
                  </li>
                </Reorder.Item>
              );
            }
          })}
        </Reorder.Group>
      </div>
      {editParagraph && (
        <EditParagraphMetaModal
          paragraph={editParagraph}
          isOpen={isEditParagraphOpen}
          onClose={() => {
            setIsEditParagraphOpen(false);
            setEditParagraph(null);
          }}
          sessionId={sessionId}
          speakers={speakers}
          onOpenSpeakersModal={onToggleSpeakersModal}
        />
      )}
      {/* Merge Modal */}
      <Modal isOpen={isMergeOpen} onOpenChange={setIsMergeOpen}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-base font-semibold">Merge selected entries</ModalHeader>
              <ModalBody className="space-y-4">
                <div className="text-sm text-onSurface/80">
                  {selectedParagraphMeta.length} entries selected. Choose the speaker to keep:
                </div>
                <Select
                  label="Speaker"
                  selectedKeys={mergeSpeakerKey ? [mergeSpeakerKey] : []}
                  onSelectionChange={(keys) => setMergeSpeakerKey(Array.from(keys)[0])}
                >
                  {selectedSpeakerOptions.map((opt) => (
                    <SelectItem key={opt.key}>{opt.label}</SelectItem>
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                {/* Same pairing as the other modals: outlined cancel,
                    AppButton for the action. */}
                <Button variant="bordered" color="danger" radius="full" onPress={onClose}>
                  Abbrechen
                </Button>
                <AppButton
                  width="w-[150px]"
                  disabled={!isSelectionContiguous || !mergeSpeakerKey}
                  onClick={handleConfirmMerge}
                >
                  Zusammenführen
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default NavigationBox;
